import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, CATEGORIES, fetchCategories, rupees } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import Header from "@/components/Header";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Search, Plus, Minus, ShieldAlert, FlaskConical, Clock, ChevronDown, X, Package } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const RECENT_KEY = "shriram.recentSalts";
const readRecents = () => {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.slice(0, 3) : [];
  } catch { return []; }
};
const pushRecent = (salt) => {
  try {
    const prev = readRecents().filter((s) => s !== salt);
    const next = [salt, ...prev].slice(0, 3);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    return next;
  } catch { return []; }
};

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [debouncedQ, setDebouncedQ] = useState(q);
  const [salts, setSalts] = useState([]);              // [{salt, count}]
  const [allCategories, setAllCategories] = useState([]); // [{id, label, count}]
  const [shelvesOpen, setShelvesOpen] = useState(false);
  const [shelvesQ, setShelvesQ] = useState("");
  const [showSug, setShowSug] = useState(false);
  const [recents, setRecents] = useState(readRecents);
  const cat = params.get("cat") || "all";
  const { items, addToCart, updateItem } = useCart() || { items: [], addToCart: () => {}, updateItem: () => {} };
  const boxRef = useRef(null);

  // Debounce the search text so we hit the server only after the shopper stops typing.
  useEffect(() => {
    const h = setTimeout(() => setDebouncedQ(q.trim()), 220);
    return () => clearTimeout(h);
  }, [q]);

  useEffect(() => {
    api.get("/medicines/salts", { params: { with_counts: 1 } })
      .then(({ data }) => setSalts(Array.isArray(data.salts) ? data.salts : []))
      .catch(() => {});
    fetchCategories().then(setAllCategories);
  }, []);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    api.get("/medicines", { params: { category: cat === "all" ? undefined : cat, q: debouncedQ || undefined } })
      .then(({ data }) => { if (!ignore) setMedicines(data); })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [cat, debouncedQ]);

  // Close dropdown on outside click
  useEffect(() => {
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setShowSug(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const setCat = (c) => {
    const p = new URLSearchParams(params);
    if (c === "all") p.delete("cat"); else p.set("cat", c);
    setParams(p, { replace: true });
  };

  const getQty = (id) => items.find((i) => i.medicine.id === id)?.quantity || 0;
  const qLower = q.trim().toLowerCase();
  const isSaltMatch = qLower && salts.some((s) => s.salt === qLower);

  const suggestions = useMemo(() => {
    if (!qLower || isSaltMatch) return [];
    return salts
      .filter((s) => s.salt.includes(qLower) && s.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }, [salts, qLower, isSaltMatch]);

  const pickSalt = (salt) => {
    setQ(salt);
    setShowSug(false);
    setRecents(pushRecent(salt));
  };

  const saltInfo = (name) => salts.find((s) => s.salt === name);
  const recentSuggestions = useMemo(
    () => recents.map(saltInfo).filter(Boolean),
    [recents, salts]
  );
  const showRecents = showSug && !qLower && recentSuggestions.length > 0;

  return (
    <div className="min-h-screen bg-[#EFE7D6]">
      <Header />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <div>
            <div className="label-caps text-stone-500">Catalogue</div>
            <h1 className="font-serif-display text-4xl lg:text-5xl text-[#1A362B] tracking-tight mt-2">Shop the pharmacy</h1>
          </div>
          <div ref={boxRef} className="relative w-full lg:w-96">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input
              placeholder="Search brand or salt (paracetamol, ibuprofen…)"
              value={q}
              onChange={(e) => { setQ(e.target.value); setShowSug(true); }}
              onFocus={() => setShowSug(true)}
              className="pl-11 h-12 rounded-full border-stone-300 bg-white focus-visible:ring-[#1A362B]"
              data-testid="search-input"
            />
            {q && q !== debouncedQ && (
              <span className="absolute right-4 top-1/2 -translate-y-1/2 label-caps text-[10px] text-stone-400 animate-pulse" data-testid="search-typing">searching…</span>
            )}
            {isSaltMatch && (
              <div className="absolute -bottom-6 left-4 text-[10px] label-caps text-[#1A362B]" data-testid="salt-match-hint">
                ✦ salt search · showing all brands containing {qLower}
              </div>
            )}
            {showSug && suggestions.length > 0 && (
              <div className="absolute z-30 mt-2 w-full bg-[#EFE7D6] border border-stone-200 rounded-md shadow-lg overflow-hidden" data-testid="salt-dropdown">
                <div className="px-4 py-2 label-caps text-[10px] text-stone-500 border-b border-stone-200 bg-white/60">
                  Search by generic salt
                </div>
                {suggestions.map((s) => (
                  <button
                    key={s.salt}
                    onClick={() => pickSalt(s.salt)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#1A362B]/5 transition-colors text-left group"
                    data-testid={`salt-suggest-${s.salt.replace(/\s+/g, "-")}`}
                  >
                    <div className="w-7 h-7 rounded-full bg-[#1A362B]/8 border border-[#1A362B]/20 flex items-center justify-center text-[#1A362B] shrink-0 group-hover:bg-[#1A362B] group-hover:text-[#EFE7D6] transition-colors">
                      <FlaskConical className="w-3.5 h-3.5"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[#1A362B] capitalize truncate">{s.salt}</div>
                      <div className="text-[10px] text-stone-500">generic salt</div>
                    </div>
                    <Badge className="bg-[#D96C4A] text-white border-0 rounded-sm font-mono-num text-[10px] shrink-0">
                      {s.count} brand{s.count === 1 ? "" : "s"}
                    </Badge>
                  </button>
                ))}
              </div>
            )}
            {showRecents && (
              <div className="absolute z-30 mt-2 w-full bg-[#EFE7D6] border border-stone-200 rounded-md shadow-lg overflow-hidden" data-testid="recent-dropdown">
                <div className="px-4 py-2 label-caps text-[10px] text-stone-500 border-b border-stone-200 bg-white/60 flex items-center gap-1">
                  <Clock className="w-3 h-3"/> Recent searches
                </div>
                {recentSuggestions.map((s) => (
                  <button
                    key={s.salt}
                    onClick={() => pickSalt(s.salt)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#1A362B]/5 transition-colors text-left group"
                    data-testid={`recent-suggest-${s.salt.replace(/\s+/g, "-")}`}
                  >
                    <div className="w-7 h-7 rounded-full bg-[#E8C170]/25 border border-[#E8C170]/50 flex items-center justify-center text-[#7c5e12] shrink-0">
                      <Clock className="w-3.5 h-3.5"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[#1A362B] capitalize truncate">{s.salt}</div>
                      <div className="text-[10px] text-stone-500">recent · tap to search again</div>
                    </div>
                    <Badge className="bg-[#1A362B] text-[#EFE7D6] border-0 rounded-sm font-mono-num text-[10px] shrink-0">
                      {s.count} brand{s.count === 1 ? "" : "s"}
                    </Badge>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-10 items-center">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              onClick={() => setCat(c.id)}
              className={`px-4 py-2 rounded-full text-sm border transition-colors label-caps ${
                cat === c.id
                  ? "bg-[#1A362B] text-[#EFE7D6] border-[#1A362B]"
                  : "bg-white text-stone-700 border-stone-300 hover:border-[#1A362B]"
              }`}
              data-testid={`cat-${c.id}`}
            >
              {c.label}
            </button>
          ))}
          <Sheet open={shelvesOpen} onOpenChange={setShelvesOpen}>
            <SheetTrigger asChild>
              <button
                className={`px-4 py-2 rounded-full text-sm border transition-colors label-caps inline-flex items-center gap-1 ${
                  allCategories.some((a) => a.id === cat) && !CATEGORIES.some((c) => c.id === cat)
                    ? "bg-[#D96C4A] text-white border-[#D96C4A]"
                    : "bg-[#EFE7D6] text-[#1A362B] border-[#1A362B]/40 hover:bg-[#1A362B] hover:text-[#EFE7D6] hover:border-[#1A362B]"
                }`}
                data-testid="more-shelves-btn"
              >
                More shelves <ChevronDown className="w-3.5 h-3.5"/>
                {allCategories.length > 0 && <span className="ml-1 opacity-70 font-mono-num text-xs">({allCategories.length})</span>}
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-md p-0 bg-[#EFE7D6] flex flex-col">
              <SheetHeader className="px-6 pt-6 pb-4 border-b border-stone-200">
                <SheetTitle className="font-serif-display text-3xl text-[#1A362B] tracking-tight">All shelves</SheetTitle>
                <p className="text-xs text-stone-500 label-caps">{allCategories.length} categories · {allCategories.reduce((s, c) => s + c.count, 0).toLocaleString()} SKUs</p>
                <div className="relative mt-3">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
                  <Input
                    value={shelvesQ}
                    onChange={(e) => setShelvesQ(e.target.value)}
                    placeholder="Filter shelves…"
                    className="pl-9 h-10 bg-white border-stone-300 focus-visible:ring-[#1A362B]"
                    data-testid="shelves-filter"
                  />
                  {shelvesQ && (
                    <button onClick={() => setShelvesQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-[#D96C4A]" aria-label="Clear">
                      <X className="w-4 h-4"/>
                    </button>
                  )}
                </div>
              </SheetHeader>
              <div className="flex-1 overflow-y-auto thin-scroll px-2 py-3">
                <button
                  onClick={() => { setCat("all"); setShelvesOpen(false); }}
                  className={`w-full flex items-center justify-between px-4 py-2.5 rounded-sm border-l-2 transition-colors text-left ${cat === "all" ? "border-[#1A362B] bg-[#1A362B]/8" : "border-transparent hover:bg-[#1A362B]/5"}`}
                  data-testid="shelf-all"
                >
                  <span className="text-sm text-[#1A362B]">All Medicines</span>
                  <Badge variant="outline" className="border-stone-300 text-stone-500 font-mono-num text-[10px]">{allCategories.reduce((s, c) => s + c.count, 0).toLocaleString()}</Badge>
                </button>
                {allCategories
                  .filter((c) => c.label.toLowerCase().includes(shelvesQ.trim().toLowerCase()))
                  .map((c) => (
                    <button
                      key={c.id}
                      onClick={() => { setCat(c.id); setShelvesOpen(false); setShelvesQ(""); }}
                      className={`w-full flex items-center justify-between px-4 py-2.5 rounded-sm border-l-2 transition-colors text-left ${cat === c.id ? "border-[#D96C4A] bg-[#D96C4A]/8" : "border-transparent hover:bg-[#1A362B]/5"}`}
                      data-testid={`shelf-${c.id}`}
                    >
                      <span className="text-sm text-[#1A362B]">{c.label}</span>
                      <Badge variant="outline" className="border-stone-300 text-stone-500 font-mono-num text-[10px]">{c.count.toLocaleString()}</Badge>
                    </button>
                  ))}
                {allCategories.filter((c) => c.label.toLowerCase().includes(shelvesQ.trim().toLowerCase())).length === 0 && (
                  <div className="py-10 text-center text-sm text-stone-500 italic">No shelf matches "{shelvesQ}"</div>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-80 rounded-md border border-stone-200 bg-white animate-pulse" />
            ))}
          </div>
        ) : medicines.length === 0 ? (
          <NotFoundRequestCard query={debouncedQ}/>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {medicines.map((m, i) => {
              const qty = getQty(m.id);
              const discount = Math.round(((m.mrp - m.price) / m.mrp) * 100);
              return (
                <motion.div
                  key={m.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i * 0.04, 0.4) }}
                  className="product-card bg-white border border-stone-200 rounded-md overflow-hidden flex flex-col"
                  data-testid={`product-card-${m.id}`}
                >
                  <Link to={`/product/${m.id}`} className="block aspect-square bg-[#FFFFFF] overflow-hidden relative">
                    <img src={m.image_url} alt={m.name} className="w-full h-full object-cover" />
                    {discount > 0 && (
                      <Badge className="absolute top-3 left-3 bg-[#D96C4A] text-white border-0 rounded-sm">{discount}% off</Badge>
                    )}
                    {m.prescription_required && (
                      <Badge className="absolute top-3 right-3 bg-[#1A362B] text-[#EFE7D6] border-0 rounded-sm gap-1">
                        <ShieldAlert className="w-3 h-3" /> Rx
                      </Badge>
                    )}
                  </Link>
                  <div className="p-4 flex-1 flex flex-col">
                    <div className="label-caps text-[10px] text-stone-500">{m.brand}</div>
                    <Link to={`/product/${m.id}`}>
                      <h3 className="font-serif-display text-lg text-[#1A362B] mt-1 leading-tight hover:text-[#D96C4A] transition-colors">{m.name}</h3>
                    </Link>
                    <div className="flex items-baseline gap-2 mt-3 font-mono-num">
                      <span className="text-lg text-[#1A362B]">{rupees(m.price)}</span>
                      {m.mrp > m.price && <span className="text-xs text-stone-400 line-through">{rupees(m.mrp)}</span>}
                    </div>
                    <div className="mt-auto pt-4">
                      {qty === 0 ? (
                        <Button
                          onClick={() => addToCart(m)}
                          disabled={m.stock === 0}
                          className="w-full rounded-full bg-[#1A362B] hover:bg-[#244A3C] text-[#EFE7D6] active:scale-95 transition-transform"
                          data-testid={`add-to-cart-${m.id}`}
                        >
                          {m.stock === 0 ? "Out of stock" : "Add to cart"}
                        </Button>
                      ) : (
                        <div className="flex items-center justify-between border border-[#1A362B] rounded-full px-1 h-10">
                          <button onClick={() => updateItem(m.id, qty - 1)} className="w-8 h-8 flex items-center justify-center hover:bg-stone-100 rounded-full" data-testid={`qty-dec-${m.id}`}><Minus className="w-4 h-4"/></button>
                          <span className="font-mono-num text-sm" data-testid={`qty-val-${m.id}`}>{qty}</span>
                          <button
                            onClick={() => {
                              if (qty >= m.stock) { toast.error("No more stock"); return; }
                              updateItem(m.id, qty + 1);
                            }}
                            className="w-8 h-8 flex items-center justify-center hover:bg-stone-100 rounded-full"
                            data-testid={`qty-inc-${m.id}`}
                          ><Plus className="w-4 h-4"/></button>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function NotFoundRequestCard({ query }) {
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!query || query.trim().length < 2) return toast.error("Type the medicine name above first");
    if (phone && !/^\d{10}$/.test(phone)) return toast.error("Phone must be 10 digits");
    setBusy(true);
    try {
      await api.post("/product-requests", { query: query.trim(), name: name.trim(), phone });
      setSent(true);
      toast.success("Request logged", { description: "We'll have it by tomorrow evening." });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send request");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="py-16 text-center border border-dashed border-[#1A362B]/30 rounded-md bg-white" data-testid="request-success">
        <div className="w-14 h-14 rounded-full bg-[#1A362B]/10 border border-[#1A362B]/30 flex items-center justify-center mx-auto text-[#1A362B]">
          <Package className="w-6 h-6"/>
        </div>
        <p className="font-serif-display text-3xl text-[#1A362B] mt-6">Noted.</p>
        <p className="text-stone-600 text-sm mt-3 max-w-sm mx-auto leading-relaxed">
          We'll source <span className="italic font-serif-display text-[#D96C4A]">{query}</span> and message you the moment it lands on the shelf — usually by tomorrow evening.
        </p>
      </div>
    );
  }

  return (
    <div className="py-16 text-center border border-dashed border-stone-300 rounded-md bg-white" data-testid="request-card">
      <p className="label-caps text-[#D96C4A] tracking-[0.28em] text-[10px]">— Not stocked yet</p>
      <p className="font-serif-display text-3xl text-[#1A362B] mt-4">
        {query ? <>We couldn't find <em className="italic">{query}</em>.</> : "Nothing on this shelf yet."}
      </p>
      <p className="text-stone-500 text-sm mt-3 max-w-md mx-auto">
        Request it and we'll source it — <span className="text-[#1A362B] font-medium">available by tomorrow evening</span>. Leave your number and we'll message when it's in.
      </p>
      <div className="mt-8 max-w-md mx-auto grid gap-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name (optional)"
            className="h-11 bg-[#EFE7D6]/40 border-stone-300"
            data-testid="request-name"
          />
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500 text-sm font-mono-num">+91</span>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
              placeholder="WhatsApp number"
              inputMode="numeric"
              maxLength={10}
              className="pl-11 h-11 bg-[#EFE7D6]/40 border-stone-300 font-mono-num"
              data-testid="request-phone"
            />
          </div>
        </div>
        <Button
          onClick={submit}
          disabled={busy}
          className="rounded-full bg-[#D96C4A] hover:bg-[#E67D5C] text-white h-11 shine"
          data-testid="request-submit"
        >
          {busy ? "Sending…" : "Request this medicine →"}
        </Button>
        <p className="text-[10px] text-stone-500 label-caps tracking-[0.2em]">Free · no obligation · WhatsApp only when it arrives</p>
      </div>
    </div>
  );
}

